import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ShoppingCart, ArrowLeft, Star, Trash2 } from 'lucide-react';
import { useCompare } from '../contexts/CompareContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ComparePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { compareItems, removeFromCompare, clearCompare } = useCompare();
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    document.title = 'Comparer - Mutagim.shop';
  }, []);

  const getImageUrl = (product) => {
    const image = product.images?.[0] || product.image;
    if (!image) return '/placeholder-product.png';
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  const handleAddToCart = (product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || product.image,
      quantity: 1,
    });
  };

  if (compareItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-mutagim-dark">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Aucun produit à comparer
            </h1>
            <p className="text-gray-400 mb-6 max-w-md">
              Ajoutez des produits à comparer en cliquant sur l'icône de comparaison sur les fiches produits.
            </p>
            <Button
              onClick={() => navigate('/products')}
              className="bg-mutagim-red hover:bg-mutagim-red/90"
            >
              Découvrir les produits
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const attributes = [
    { key: 'price', label: 'Prix', render: (p) => formatPrice(p.price) },
    { key: 'sizes', label: 'Tailles disponibles', render: (p) => {
      const sizes = p.sizes || p.variants?.map(v => v.size).filter(Boolean) || [];
      return sizes.length > 0 ? sizes.join(', ') : '-';
    }},
    { key: 'rating', label: 'Note', render: (p) => (
      <div className="flex items-center gap-1">
        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
        <span>{p.rating?.toFixed(1) || '-'}</span>
        {p.reviews_count > 0 && (
          <span className="text-gray-500 text-sm">({p.reviews_count})</span>
        )}
      </div>
    )},
    { key: 'stock', label: 'Stock', render: (p) => (
      <span className={p.stock > 0 ? 'text-green-500' : 'text-red-500'}>
        {p.stock > 0 ? `${p.stock} en stock` : 'Rupture'}
      </span>
    )},
    { key: 'description', label: 'Description', render: (p) => (
      <p className="text-sm text-gray-400 line-clamp-3">
        {typeof p.description === 'object' ? (p.description.fr || p.description.en || '') : (p.description || '-')}
      </p>
    )},
  ];

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="compare-page">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-white">
              Comparer ({compareItems.length} produits)
            </h1>
          </div>
          <Button
            variant="outline"
            onClick={clearCompare}
            className="text-gray-400 border-gray-700 hover:bg-gray-800"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Tout effacer
          </Button>
        </div>

        {/* Comparaison Grid */}
        <div className="bg-mutagim-card rounded-xl overflow-hidden border border-gray-800">
          {/* Header Row - Images */}
          <div className="grid border-b border-gray-800" style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}>
            <div className="p-4 bg-gray-900/50"></div>
            {compareItems.map(product => (
              <div key={product.id} className="p-4 relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFromCompare(product.id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500 z-10"
                  data-testid={`remove-compare-${product.id}`}
                >
                  <X className="w-4 h-4" />
                </Button>
                <Link to={`/product/${product.slug}`}>
                  <img
                    src={getImageUrl(product)}
                    alt={typeof product.name === 'object' ? product.name.fr : product.name}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <h3 className="font-semibold text-white text-center line-clamp-2">
                    {typeof product.name === 'object' ? product.name.fr : product.name}
                  </h3>
                </Link>
              </div>
            ))}
          </div>

          {/* Attribute Rows */}
          {attributes.map((attr, idx) => (
            <div 
              key={attr.key} 
              className={`grid border-b border-gray-800 ${idx % 2 === 0 ? 'bg-gray-900/30' : ''}`}
              style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}
            >
              <div className="p-4 font-medium text-gray-300 flex items-center">
                {attr.label}
              </div>
              {compareItems.map(product => (
                <div key={product.id} className="p-4 text-white flex items-center justify-center">
                  {attr.render(product)}
                </div>
              ))}
            </div>
          ))}

          {/* Action Row */}
          <div 
            className="grid bg-gray-900/50" 
            style={{ gridTemplateColumns: `200px repeat(${compareItems.length}, 1fr)` }}
          >
            <div className="p-4"></div>
            {compareItems.map(product => (
              <div key={product.id} className="p-4 flex justify-center">
                <Button
                  onClick={() => handleAddToCart(product)}
                  className="bg-mutagim-red hover:bg-mutagim-red/90"
                  disabled={!product.stock || product.stock === 0}
                  data-testid={`add-cart-compare-${product.id}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Ajouter au panier
                </Button>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ComparePage;
